import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Info, X } from "lucide-react";
import { cn } from "../../lib/utils";

interface InfoSheetProps {
  /** Title shown at the top of the sheet. */
  title: string;
  /** Detailed content rendered inside the scrollable body. */
  children: React.ReactNode;
  /** The element that opens the sheet (defaults to a standard Info icon button). */
  trigger?: React.ReactNode;
  /** Accessible label for the default trigger button. */
  ariaLabel?: string;
  /** Extra classes for the default trigger button. */
  className?: string;
  /** Extra classes for the default Info icon. */
  iconClassName?: string;
  /** Optional controlled open state. */
  open?: boolean;
  /** Optional controlled open-change handler. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * App-wide bottom sheet for "Info" icons. Tapping the icon slides up a sheet
 * with detailed information, keeping the experience consistent everywhere
 * (e.g. Profile → BMI). Pass a custom `trigger` to reuse the sheet behaviour
 * with an existing icon/button.
 */
export const InfoSheet: React.FC<InfoSheetProps> = ({
  title,
  children,
  trigger,
  ariaLabel,
  className,
  iconClassName,
  open,
  onOpenChange,
}) => (
  <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <DialogPrimitive.Trigger asChild>
      {trigger ?? (
        <button
          type="button"
          aria-label={ariaLabel ?? `About ${title}`}
          className={cn(
            "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground",
            className,
          )}
        >
          <Info className={cn("h-4 w-4", iconClassName)} />
        </button>
      )}
    </DialogPrimitive.Trigger>

    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogPrimitive.Content
        onPointerDownOutside={(event) => event.preventDefault()}
        className="fixed inset-x-0 bottom-0 z-[101] mx-auto flex max-h-[85vh] w-full max-w-[400px] flex-col rounded-t-3xl border border-border bg-background shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=closed]:duration-200 data-[state=open]:duration-300"
      >
        {/* Grab handle */}
        <div className="mx-auto mt-3 h-1.5 w-10 shrink-0 rounded-full bg-border" aria-hidden />

        <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-3">
          <DialogPrimitive.Title className="text-base font-bold text-foreground">
            {title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Close className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </div>

        <div className="overflow-y-auto px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-1 text-sm leading-relaxed text-muted-foreground">
          {children}
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
);

export default InfoSheet;
